import {Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {finalize, map, Observable, tap} from 'rxjs';

import {
    AccountInfoData,
    AccountInformation,
    AccountPasswords, ApiTokenData, Service24PolicyData
} from "../../models";
import {SettingsService} from "./settings.service";
import {UserService} from '../user.service';
import {SubtitlesService} from "../subtitles.service";
import {AppAlertService} from "../app-alert.service";

@Injectable({
    providedIn: 'root'
})
export class SettingsAccountService {
    accountInformation: AccountInformation = {
        accountEmail: '',
        accountName: '',
        regulamin_serwis24: "f",
        oldPassword: '',
        newPassword: '',
        confirmedPassword: '',
    }
    newAccountEmail:string = '';
    readonly service24PolicyAccepted = signal<boolean | null>(null);
    showDialog: string = '';
    apiToken: string = '';
    generatingApiToken: boolean = false;

    constructor(
        private appAlert: AppAlertService,
        private http: HttpClient,
        private settings: SettingsService,
        private subtitles: SubtitlesService,
        private user: UserService,
    ) {
    }

    getAccountInformation(): void {
        this.http.get<AccountInfoData>(`/api/v1/users/${this.user.user_id}/info`)
            .subscribe((response) => {
                const policyAccepted = this.service24PolicyAccepted() === true
                    || response.regulamin_serwis24 === 't';
                this.newAccountEmail = response.accountEmail;
                this.accountInformation = {
                    oldPassword: '',
                    newPassword: '',
                    confirmedPassword: '',
                    ...response,
                    regulamin_serwis24: policyAccepted ? 't' : 'f'
                };
                this.service24PolicyAccepted.set(policyAccepted);
                this.settings.getLastModification();
            });
    }

    checkAccountEmail(email: string): boolean {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return regex.test(email);
    }

    changeAccountEmail(): void {
        const newEmail = this.newAccountEmail;
        const validated = this.checkAccountEmail(newEmail);

        if (validated && newEmail !== this.accountInformation.accountEmail) {
            this.http.put(`/api/v1/users/${this.user.user_id}/email/i18n/${this.subtitles.language}`, {newEmail})
                .subscribe({
                    next: () => {
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(3391),
                            message: this.subtitles.setSubtitle(3393),
                            okButtonText: this.subtitles.setSubtitle(475),
                        });
                    },
                    error: (error) => {
                        const messageId = (error.status === 422 && error.error.error.code === -1) ? 1445 : 1941;
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(3391),
                            message: this.subtitles.setSubtitle(messageId),
                            okButtonText: this.subtitles.setSubtitle(1136),
                        });
                    }
                });
        } else if (!validated) {
            this.appAlert.openAlertDialog({
                title: this.subtitles.setSubtitle(3391),
                message: this.subtitles.setSubtitle(1342),
                okButtonText: this.subtitles.setSubtitle(1136),
            });
        }
    }

    checkPassword(type: keyof AccountPasswords): boolean {
        if (type === 'oldPassword') {
            return this.accountInformation.oldPassword.length >= 8;
        } else if (type === 'newPassword') {
            return this.accountInformation.newPassword.length >= 8
                && this.accountInformation.newPassword !== this.accountInformation.oldPassword;
        } else {
            return this.accountInformation.newPassword === this.accountInformation.confirmedPassword
                && this.accountInformation.confirmedPassword.length >= 8;
        }
    }

    private checkErrors(type: string, errors: number[]): void {
        if (type === 'oldPassword' && this.accountInformation.oldPassword.length < 8) {
            errors.push(1147);
        }
        if (type === 'newPassword') {
            if (this.accountInformation.newPassword.length < 8) {
                errors.push(1147);
            }
            if (this.accountInformation.newPassword === this.accountInformation.oldPassword) {
                errors.push(1932)
            }
        }
        if (type === 'confirmedPassword' &&
            this.accountInformation.newPassword !== this.accountInformation.confirmedPassword) {
            errors.push(1341);
        }
    }

    hasPasswords(): boolean {
        return Boolean(this.accountInformation.oldPassword.length
            && this.accountInformation.newPassword.length
            && this.accountInformation.confirmedPassword.length)
    }

    private checkPasswords(): boolean {
        if (this.hasPasswords()) {
            return this.checkPassword('oldPassword')
                && this.checkPassword('newPassword')
                && this.checkPassword('confirmedPassword');
        }

        return false;
    }

    changePassword(): void {
        const validated = this.checkPasswords();

        if (validated) {
            const data = {
                oldPassword: this.accountInformation.oldPassword,
                newPassword: this.accountInformation.newPassword,
            };

            this.http.put(`/api/v1/users/${this.user.user_id}/password`, data)
                .subscribe({
                    next: () => {
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(1949),
                            message: this.subtitles.setSubtitle(1940),
                            okButtonText: this.subtitles.setSubtitle(475),
                        });
                        this.accountInformation.oldPassword = '';
                        this.accountInformation.newPassword = '';
                        this.accountInformation.confirmedPassword = '';
                    },
                    error: (error) => {
                        const messageId = (error.status === 422 && error.error.error.code === -1) ? 1349 : 1941;
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(1949),
                            message: this.subtitles.setSubtitle(messageId),
                            okButtonText: this.subtitles.setSubtitle(1136),
                        });
                        this.openDialogForm('password');
                    }
                })
        } else {
            this.showValidationErrors();
        }
    }

    private showValidationErrors(): void {
        const errors: number[] = [];

        this.checkErrors('oldPassword', errors);
        this.checkErrors('newPassword', errors);
        this.checkErrors('confirmedPassword', errors);

        let errorsNumber = 0;
        let messages: string[] = [];

        for (const error of errors) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(error)}.`);
        }
        this.appAlert.openAlertDialog({
            title: this.subtitles.setSubtitle(1949),
            message: messages,
            okButtonText: this.subtitles.setSubtitle(1136),
            okFunction: this.openDialogForm.bind(this, 'password'),
        });
    }

    openDialogForm(dialogName: string): void {
        this.showDialog = dialogName;
    }

    closeDialogForm(): void {
        this.showDialog = '';
        if (this.apiToken) {
            this.apiToken = '';
        }
    }

    generateApiToken(): void {
        if (this.generatingApiToken) {
            return;
        }

        this.generatingApiToken = true;
        this.http.post<ApiTokenData>(`/api/v1/users/${this.user.user_id}/api_token`, {})
            .pipe(finalize(() => this.generatingApiToken = false))
            .subscribe({
                next: (response) => {
                    this.apiToken = response.token;
                    this.openDialogForm('apiToken');
                },
                error: () => {
                    this.appAlert.openAlertDialog({
                        title: this.subtitles.setSubtitle(17940),
                        message: this.subtitles.setSubtitle(1941),
                        okButtonText: this.subtitles.setSubtitle(1136),
                    });
                }
            });
    }

    saveService24policy(acceptService24Policy: boolean): Observable<boolean> {
        const data = {acceptService24Policy};
        return this.http.put<Service24PolicyData>(`/api/v1/users/${this.user.user_id}/service24_policy`, data)
            .pipe(
                map((response) => response.policy),
                tap((policyAccepted) => {
                    const accepted = this.service24PolicyAccepted() === true || policyAccepted;
                    this.accountInformation.regulamin_serwis24 = accepted ? 't' : 'f';
                    this.service24PolicyAccepted.set(accepted);
                })
            );
    }
}
